import {useCallback, useEffect, useState} from 'react';
import {apiRegistry} from './apiRegistry';

/**
 * Hook that subscribes to the API registry and provides the settled state
 * @param {Object} options - Hook options
 * @param {number} [options.debounceTime] - Optional custom debounce time to set
 * @returns {Object} - Settled state and related functions
 * @returns {boolean} .isSettled - True when all API requests are complete
 * @returns {Function} .setDebounceTime - Function to change the debounce time
 * @returns {Object} .activeRequests - Current active requests if not settled
 */
export const useApiSettled = (options = {}) => {
	const [isSettled, setIsSettled] = useState(apiRegistry.isSettled());
	const [activeRequests, setActiveRequests] = useState(apiRegistry.getActiveRequests());

	// Apply custom debounce time if provided in options
	useEffect(() => {
		if (options.debounceTime !== undefined) {
			apiRegistry.setDebounceTime(options.debounceTime);
		}
	}, [options.debounceTime]);

	useEffect(() => {
		// Subscribe to settled state
		const settledSubscription = apiRegistry.settled$.subscribe((settled) => {
			setIsSettled(settled);
		});

		// Subscribe to active requests
		const requestsSubscription = apiRegistry.activeRequests$.subscribe((requests) => {
			setActiveRequests(Object.fromEntries(requests));
		});

		return () => {
			settledSubscription.unsubscribe();
			requestsSubscription.unsubscribe();
		};
	}, []);

	// Function to set debounce time
	const setDebounceTime = useCallback((ms) => {
		apiRegistry.setDebounceTime(ms);
	}, []);

	return {
		isSettled,
		activeRequests,
		setDebounceTime
	};
};

export default useApiSettled;
